import React from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { 
  IconDelete,
  IconEdit,
  IconCheckDark,
} from "../../../assets/images/icons";
import { 
  ResponsiveTable,
  ErrorBoundary,
  Loaders,
} from "../..";
import {
  ActionButton,
  TextButton
} from "../../buttons";
import {
  OverviewSubheader
} from "../../layouts";
import {
  useAffiliationsList,
  deleteAffiliation,
  approveAffiliation,
} from "./../../../queries/company";
import {
  Form as AffiliationForm,
} from ".";
import titleize from "underscore.string/titleize";
import { useSessionContext } from "./../../../contexts/SessionContext";
import { CompanyContext } from "./../../../contexts/CompanyContext";
import { PortalizedModalContext } from "./../../PortalizedModal";
import { renderRailsErrors } from "../../../utils/rails_error_parser";

export default function Affiliations({companyId}) {
  const queryClient = useQueryClient();
  const affiliations = useAffiliationsList({company_id: companyId})
  const { selectedCompany } = React.useContext(CompanyContext);
  const { isPermitted, ACTIONS } = useSessionContext();
  const affiliationModal = React.useContext(PortalizedModalContext);
  const confirmationModal = React.useContext(PortalizedModalContext);

  const deleteAffiliationMutation = useMutation(
    deleteAffiliation,
    {
      onSuccess: (affiliation) => {
        confirmationModal.closeModal();
        toast.success('Affiliation deleted.');
        queryClient.invalidateQueries(
          ["company-affilations-list", companyId],
          {
            exact: true,
          }
        )
      },
      onError: (error) => {
        confirmationModal.closeModal();
        renderRailsErrors(error)
      },
    }
  )

 
  const affiliationDeleteHandler = (affiliation) => {
    confirmationModal.setModalContent(
      null,
      <div>
        <div className="px-3">
          <p>Are you sure you want to delete affiliation?</p>
        </div>
        <div className="p-3 text-right">
          <button className="btn btn-dark rounded-0 px-3"
            onClick={() => {
              deleteAffiliationMutation.mutate({company_id: selectedCompany.slug, affiliation_id: affiliation.id})
            }}
          >
            Yes, delete affiliation.
          </button>
        </div>
      </div>
    );
    confirmationModal.openModal()
  }

  const approveAffiliationMutation = useMutation(
    approveAffiliation,
    {
      onSuccess: (affiliation) => {
        confirmationModal.closeModal();
        toast.success('Affiliation approved.');
        queryClient.invalidateQueries(
          ["company-affilations-list", companyId],
          {
            exact: true,
          }
        )
      },
      onError: (error) => {
        confirmationModal.closeModal();
        renderRailsErrors(error)
      },
    }
  )

  const affiliationApproveHandler = (affiliation) => {
    confirmationModal.setModalContent(
      null,
      <div>
        <div className="px-3">
          <p>Are you sure you want to approve affiliation?</p>
        </div>
        <div className="p-3 text-right">
          <button className="btn btn-dark rounded-0 px-3"
            onClick={() => {
              approveAffiliationMutation.mutate({company_id: selectedCompany.slug, affiliation_id: affiliation.id})
            }}
          >
            Yes, approve affiliation.
          </button>
        </div>
      </div>
    );
    confirmationModal.openModal()
  }

  const tableData = React.useMemo(
    () => {
      if (affiliations.isSuccess)
        return affiliations.data
      else
        return []
    },
    [affiliations]
  );

  const columns = React.useMemo(
    () => {
      if (affiliations.isSuccess){
        return [
          { 
            Header: 'Member name',
            accessor: 'username',
            Cell: ({value}) => titleize(value),
          },
          { 
            Header: 'Email',
            accessor: 'email',
          },
          {
            Header: 'Title',
            accessor: 'title',
            Cell: ({value}) => titleize(value),
          },
          {
            Header: '',
            accessor: 'id',
            Cell: ({value, row}) => (
              isPermitted(ACTIONS.WRITE_UPDATE_AFFILIATIONS, selectedCompany.id) ?
                <div className="table-action-items-container justify-content-end">
                  {
                    row.original.approved ?
                      <ActionButton icon={IconEdit} clickHandler={() => {editAffiliation(row.original)}} />
                      :
                      <ActionButton icon={IconCheckDark} clickHandler={() => {affiliationApproveHandler(row.original)}} />
                  }
                  <ActionButton icon={IconDelete} clickHandler={() => {affiliationDeleteHandler(row.original)}} />
                </div> 
                :
                null
            )
          }
        ]
      }
      else{
        return []
      }
    },
    [affiliations]
  );

  if (affiliations.isLoading)
    return <Loaders.ListPageLoader />

  const createAffiliation = () => {
    affiliationModal.setModalContent(
      "Add Affiliation",
      <AffiliationForm companyId={selectedCompany.slug} affiliation={{}} onSuccess={affiliationModal.closeModal}/>
    )
    affiliationModal.openModal()
  }

  const editAffiliation = (affiliation) => {
    affiliationModal.setModalContent(
      "Update Affiliation",
      <AffiliationForm companyId={selectedCompany.slug} affiliation={affiliation} onSuccess={affiliationModal.closeModal}/>
    )
    affiliationModal.openModal()
  }

  return (
    <ErrorBoundary>
      <OverviewSubheader
        left='Affiliations'
        right={
          isPermitted(ACTIONS.WRITE_UPDATE_AFFILIATIONS, selectedCompany.id) &&
            <TextButton title="Add Affiliation" onClick={createAffiliation}/>
        }
        tooltipDesc="Affiliation badges show next to a user's name on the discussion board. Any person affiliated with your company participating on your offer's discussion board needs to be identified with an affiliation. You can add affiliations here and approve affiliations submitted by users."
      />
      <ResponsiveTable title="Affiliations" columns={columns} data={tableData} />
    </ErrorBoundary>
  );
}
